import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import * as Portal from '@radix-ui/react-portal';
import { CtaForm } from '@pairy/sheard/types';

import { v2IsThumbnailPanelOpenAtom } from '../../../../store/v2/viewer.atom';
import useUpdateViewerPageState from '../../../../hooks/useUpdateViewerPageState';
import {
  isCtaModalOpenAtom,
  isDisableHelpersAtom,
} from '../../../../store/index.atom';
import MobilePageWrapper from '../../../@common/PageWrapper/Mobile';

import * as Styled from './index.styles';
import CtaFormModal from './CtaFormModal';
import FormOpenButton from './FormOpenButton';

interface Props {
  ctaForm?: CtaForm;
  isPreview: boolean;
  totalPage: number;
  isNextPageButtonDisabled: boolean;
  isEmailVerificationEnabled: boolean;
  isFormSubmitted?: boolean;
  translateKoToEn: (item: string) => string;
  userSubscriptionPlan: string;
  isMobileLandscape?: boolean;
}

const CtaFormLayer = ({
  ctaForm,
  isPreview,
  totalPage,
  isNextPageButtonDisabled,
  isEmailVerificationEnabled,
  isFormSubmitted,
  translateKoToEn,
  userSubscriptionPlan,
  isMobileLandscape = false,
}: Props) => {
  const [isOpenModal, setIsOpenModal] = useAtom(isCtaModalOpenAtom);
  const isDisableHelpers = useAtomValue(isDisableHelpersAtom);
  const isDrawerOpen = useAtomValue(v2IsThumbnailPanelOpenAtom);

  const { updateCurrentPage, currentPage } = useUpdateViewerPageState();

  const ctaFormPage = ctaForm?.page;
  const blindMode = ctaForm?.blind;

  const isNoneBlindMode = blindMode === 'none';
  const isCtaFormPageAndNoneBlindMode =
    ctaFormPage === currentPage && isNoneBlindMode;

  useEffect(() => {
    if (isNoneBlindMode) {
      setIsOpenModal(false);
    } else {
      const isOpen = ctaFormPage === currentPage && !isFormSubmitted;

      setIsOpenModal(isOpen);
    }
  }, [isNoneBlindMode, currentPage, ctaFormPage, isFormSubmitted]);

  /*폼을 입력해야하는 상태에서 폼 모달을 닫았을 때  */
  const closeModal = (isMutateSuccess = false) => {
    if (!isFormSubmitted && !isMutateSuccess) {
      /*특정 페이지만 가리고 마지막 페이지 인 경우 , 이전 페이지로 이동 */
      if (blindMode === 'specific' && totalPage === currentPage) {
        updateCurrentPage(currentPage - 1);

        /*특정 페이지만 가리고 마지막 페이지가 아닌 경우, 다음 페이지로 이동 */
      } else if (blindMode === 'specific') {
        updateCurrentPage(currentPage + 1);

        /*특정 페이지 이후 모두 가리는 경우, 이전 페이지로 이동 */
      } else if (blindMode === 'startFrom') {
        updateCurrentPage(currentPage - 1);
      }
    }
    setIsOpenModal(false);
  };

  const openModal = () => {
    setIsOpenModal(true);
  };

  return (
    <>
      {isOpenModal && (
        <Portal.Root
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <MobilePageWrapper
            totalPage={totalPage}
            isNextPageButtonDisabled={isNextPageButtonDisabled}
          >
            <Styled.StackWrapper
              direction="column"
              padding={{ top: 0, bottom: 107.5 }}
            >
              <CtaFormModal
                ctaForm={ctaForm}
                isEmailVerificationEnabled={isEmailVerificationEnabled}
                isPreview={isPreview}
                closeModal={closeModal}
                translateKoToEn={translateKoToEn}
                userSubscriptionPlan={userSubscriptionPlan}
              />
            </Styled.StackWrapper>
          </MobilePageWrapper>
        </Portal.Root>
      )}
      {isCtaFormPageAndNoneBlindMode && (
        <Styled.FlexBottomWrapper
          isMobileLandscape={isMobileLandscape}
          isDisableHelpers={isDisableHelpers}
          isDrawerOpen={isDrawerOpen}
        >
          <FormOpenButton
            openModal={openModal}
            buttonText={ctaForm?.name ?? ''}
          />
        </Styled.FlexBottomWrapper>
      )}
    </>
  );
};

export default CtaFormLayer;
